import { captureException } from '@/utils/sentry';
import { isMinMaxQuantityEnabled } from '@/hooks/use-min-max-quantity-enabled';
import {
  LocalScanCatalogRefreshTrigger,
  SpanAttribute,
} from '@/utils/sentry/attributes';
import {
  OfflineMinimalVariantPackAdapter,
  OfflineVisibleVariantPackIDAdapter,
  VariantPackEntry,
} from '@/adapters/offline-mode';
import { startSpan } from '@/utils/sentry/platform';
import { useCallback, useRef, useState } from 'react';
import { useDownloadMinimalVariantPacks } from './use-download-minimal-variant-packs';
import { useDynamicConfigValue } from '@/hooks/use-dynamic-config-value';
import { useFocusEffect } from '@react-navigation/native';
import { useUserContext } from '@/contexts/user-context';

export enum ScannerStaleness {
  Fresh = 'fresh',
  None = 'none',
  Stale3Days = 'stale-3days',
  Stale7Days = 'stale-7days',
}

const MS_PER_DAY = 24 * 60 * 60 * 1000;
const STALE_DAYS = 3;
const VERY_STALE_DAYS = 7;

// Checks both the supplier catalog and the visible VP ids of the current chat for staleness
// Will be considered stale if _either_ of them are stale
function getStaleness(
  supplierCatalogDownloadedAt: string | null,
  visibleVPIDsDownloadedAt: string | null,
): ScannerStaleness {
  if (!supplierCatalogDownloadedAt || !visibleVPIDsDownloadedAt) {
    return ScannerStaleness.None;
  }

  const olderMs = Math.min(
    new Date(supplierCatalogDownloadedAt).getTime(),
    new Date(visibleVPIDsDownloadedAt).getTime(),
  );
  const ageMs = Date.now() - olderMs;

  if (ageMs > VERY_STALE_DAYS * MS_PER_DAY) {
    return ScannerStaleness.Stale7Days;
  }
  if (ageMs > STALE_DAYS * MS_PER_DAY) {
    return ScannerStaleness.Stale3Days;
  }

  return ScannerStaleness.Fresh;
}

interface ScanDataState {
  codeIndex: Map<string, string[]>;
  entriesByUUID: Map<string, VariantPackEntry>;
  isLoaded: boolean;
}

function stripOrderConstraints(entry: VariantPackEntry): VariantPackEntry {
  const stripped = { ...entry };
  delete stripped.order_max_quantity;
  delete stripped.order_min_quantity;
  return stripped;
}

async function loadScanData(
  supplierUUID: string,
  restaurantUUID: string,
  minMaxQuantityEnabled: boolean,
): Promise<ScanDataState> {
  const minimalVPAdapter =
    OfflineMinimalVariantPackAdapter.getInstance(supplierUUID);
  const vpIdAdapter = OfflineVisibleVariantPackIDAdapter.getInstance(
    restaurantUUID,
    supplierUUID,
  );

  const [entries, vpIds] = await Promise.all([
    minimalVPAdapter.fetch(),
    vpIdAdapter.fetch(),
  ]);

  const visibleVPIDs = vpIds ? new Set(vpIds) : new Set<string>();

  if (!entries) {
    return {
      codeIndex: new Map(),
      entriesByUUID: new Map(),
      isLoaded: true,
    };
  }

  const codeIndex = new Map<string, string[]>();
  const entriesByUUID = new Map<string, VariantPackEntry>();

  // single pass of entries to populate both
  for (const cachedEntry of entries) {
    if (!visibleVPIDs.has(cachedEntry.uuid)) {
      continue;
    }

    const entry = minMaxQuantityEnabled
      ? cachedEntry
      : stripOrderConstraints(cachedEntry);

    entriesByUUID.set(entry.uuid, entry);

    // dedupe since VP's external_item_id can equal a UPC (or a UPC can be listed twice for some reason)
    const codes = new Set<string>();

    if (entry.external_item_id) {
      codes.add(entry.external_item_id.toLowerCase());
    }

    if (entry.upc_list) {
      for (const upc of entry.upc_list) {
        if (upc) {
          codes.add(upc.toLowerCase());
        }
      }
    }

    for (const code of codes) {
      const existing = codeIndex.get(code);
      if (existing) {
        existing.push(entry.uuid);
      } else {
        codeIndex.set(code, [entry.uuid]);
      }
    }
  }

  return { codeIndex, entriesByUUID, isLoaded: true };
}

export function refreshEntriesFromCatalog(
  entries: VariantPackEntry[],
  entriesByUUID: Map<string, VariantPackEntry>,
): VariantPackEntry[] {
  let changed = false;
  const refreshed = entries.map((entry) => {
    const reloaded = entriesByUUID.get(entry.uuid);
    if (reloaded && reloaded !== entry) {
      changed = true;
      return reloaded;
    }
    return entry;
  });

  return changed ? refreshed : entries;
}

export interface LocalScanData {
  cancelDownload: () => void;
  /** Map from lowercased code (UPC or external_item_id) to variant pack UUIDs */
  codeIndex: Map<string, string[]>;
  downloadProgress: number;
  entriesByUUID: Map<string, VariantPackEntry>;
  /** Load entries from AsyncStorage on-demand. Returns the loaded state. */
  fetchEntries: () => Promise<ScanDataState>;
  hasData: boolean;
  hasTriedAutoSync: boolean;
  isDownloading: boolean;
  isLoaded: boolean;
  staleness: ScannerStaleness | null;
  startSync: (trigger?: LocalScanCatalogRefreshTrigger) => Promise<void>;
}

const EMPTY_CODE_INDEX = new Map<string, string[]>();
const EMPTY_ENTRIES = new Map<string, VariantPackEntry>();
const EMPTY_SCAN_DATA: ScanDataState = {
  codeIndex: EMPTY_CODE_INDEX,
  entriesByUUID: EMPTY_ENTRIES,
  isLoaded: false,
};

export const useLocalScanData = ({
  autoDownload = false,
  loadEntries = false,
}: { autoDownload?: boolean; loadEntries?: boolean } = {}): LocalScanData => {
  const userContext = useUserContext();
  const {
    isGuestMode,
    selectedRestaurantUUID: restaurantUUID,
    selectedRestaurantRelationship: { supplierUUID },
  } = userContext;

  const {
    downloadVisibleVariantPackIDs,
    fetchMinimalVariantPacks,
    getQueryKey,
  } = useDownloadMinimalVariantPacks();
  const queryKey = getQueryKey(supplierUUID);

  const employeeRole =
    'supplierEmployeeRole' in userContext
      ? userContext.supplierEmployeeRole
      : null;
  const orderQuantityConfig = useDynamicConfigValue('order_quantity_config');
  const minMaxQuantityEnabled = isMinMaxQuantityEnabled({
    employeeRole,
    orderQuantityConfig,
  });

  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState(0);
  // null = staleness check hasn't completed yet (async), distinct from None = no data
  const [staleness, setStaleness] = useState<ScannerStaleness | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const hasTriedAutoSync = useRef(false);

  const [codeIndex, setCodeIndex] = useState(new Map<string, string[]>());
  const [entriesByUUID, setEntriesByUUID] = useState(
    new Map<string, VariantPackEntry>(),
  );
  const [isLoaded, setIsLoaded] = useState(false);

  const applyScanData = useCallback((data: ScanDataState) => {
    setCodeIndex(data.codeIndex);
    setEntriesByUUID(data.entriesByUUID);
    setIsLoaded(data.isLoaded);
  }, []);

  const fetchEntries = useCallback(async () => {
    const data = await loadScanData(
      supplierUUID,
      restaurantUUID,
      minMaxQuantityEnabled,
    );
    applyScanData(data);
    return data;
  }, [supplierUUID, restaurantUUID, minMaxQuantityEnabled, applyScanData]);

  const startSync = useCallback(
    async (trigger = LocalScanCatalogRefreshTrigger.FirstOpen) => {
      // Cancel any in-flight download before starting a new one
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      const controller = new AbortController();
      abortControllerRef.current = controller;
      setIsDownloading(true);
      setDownloadProgress(0);

      await startSpan(
        {
          attributes: {
            [SpanAttribute.LocalScanCatalogRefreshTrigger]: trigger,
          },
          forceTransaction: true,
          name: 'local_scan.catalog_refresh',
          op: 'local_scan.catalog',
        },
        async (rootSpan) => {
          try {
            const [supplierResult, visibleResult] = await Promise.all([
              fetchMinimalVariantPacks(
                supplierUUID,
                (downloaded, total) => {
                  if (controller.signal.aborted) {
                    return;
                  }
                  const pct =
                    total > 0 ? Math.round((downloaded / total) * 100) : 0;
                  setDownloadProgress(pct);
                },
                controller.signal,
              ),
              downloadVisibleVariantPackIDs(
                restaurantUUID,
                supplierUUID,
                controller.signal,
              ),
            ]);

            if (!controller.signal.aborted) {
              rootSpan.setAttributes({
                [SpanAttribute.LocalScanCatalogRefreshSupplierCatalogCount]:
                  supplierResult.count,
                [SpanAttribute.LocalScanCatalogRefreshSupplierCatalogSize]:
                  supplierResult.estimatedSize,
                [SpanAttribute.LocalScanCatalogRefreshVisibleVariantPackCount]:
                  visibleResult.count,
                [SpanAttribute.LocalScanCatalogRefreshVisibleVariantPackIdsSize]:
                  visibleResult.estimatedSize,
              });

              setDownloadProgress(100);
              setStaleness(ScannerStaleness.Fresh);

              if (loadEntries) {
                const data = await loadScanData(
                  supplierUUID,
                  restaurantUUID,
                  minMaxQuantityEnabled,
                );
                applyScanData(data);
              }
            }
          } catch (e) {
            if (!controller.signal.aborted) {
              captureException(e, {
                extra: { restaurantUUID, supplierUUID },
              });
              rootSpan.setStatus({ code: 2, message: 'internal_error' });
            } else {
              rootSpan.setStatus({ code: 0, message: 'cancelled' });
            }
          } finally {
            // Only clean up if this controller is still the active one
            // (a new startSync call may have replaced it)
            if (abortControllerRef.current === controller) {
              setIsDownloading(false);
              abortControllerRef.current = null;
            }
          }
        },
      );
    },
    [
      applyScanData,
      downloadVisibleVariantPackIDs,
      fetchMinimalVariantPacks,
      loadEntries,
      minMaxQuantityEnabled,
      restaurantUUID,
      supplierUUID,
    ],
  );

  const startSyncRef = useRef(startSync);
  startSyncRef.current = startSync;

  const cancelDownload = useCallback(() => {
    abortControllerRef.current?.abort();
    abortControllerRef.current = null;
    setIsDownloading(false);
  }, []);

  // Check staleness on focus, load scan data, auto-download if no data exists or query has changed
  useFocusEffect(
    useCallback(() => {
      if (isGuestMode) {
        return;
      }

      const checkAdaptersAndLoadData = async () => {
        const minimalVPAdapter =
          OfflineMinimalVariantPackAdapter.getInstance(supplierUUID);
        const vpIdAdapter = OfflineVisibleVariantPackIDAdapter.getInstance(
          restaurantUUID,
          supplierUUID,
        );

        const [minimalVPDate, vpIdDate, storedQueryKey] = await Promise.all([
          minimalVPAdapter.getDownloadedAt(),
          vpIdAdapter.getDownloadedAt(),
          autoDownload ? minimalVPAdapter.getQueryKey() : null,
        ]);

        const currentStaleness = getStaleness(minimalVPDate, vpIdDate);
        setStaleness(currentStaleness);

        if (loadEntries) {
          try {
            const data = await loadScanData(
              supplierUUID,
              restaurantUUID,
              minMaxQuantityEnabled,
            );
            applyScanData(data);
          } catch (e) {
            captureException(e);
          }
        }

        if (autoDownload && !hasTriedAutoSync.current) {
          const queryChanged = storedQueryKey !== queryKey;

          if (currentStaleness === ScannerStaleness.None || queryChanged) {
            hasTriedAutoSync.current = true;
            startSyncRef.current(
              queryChanged
                ? LocalScanCatalogRefreshTrigger.QueryChanged
                : LocalScanCatalogRefreshTrigger.FirstOpen,
            );
          }
        }
      };

      checkAdaptersAndLoadData();
    }, [
      isGuestMode,
      restaurantUUID,
      supplierUUID,
      autoDownload,
      loadEntries,
      minMaxQuantityEnabled,
      applyScanData,
      queryKey,
    ]),
  );

  if (isGuestMode) {
    return {
      cancelDownload: () => {},
      codeIndex: EMPTY_CODE_INDEX,
      downloadProgress: 0,
      entriesByUUID: EMPTY_ENTRIES,
      fetchEntries: async () => EMPTY_SCAN_DATA,
      hasData: false,
      hasTriedAutoSync: false,
      isDownloading: false,
      isLoaded: false,
      staleness: null,
      startSync: async () => {},
    };
  }

  return {
    cancelDownload,
    codeIndex,
    downloadProgress,
    entriesByUUID,
    fetchEntries,
    hasData: staleness !== null && staleness !== ScannerStaleness.None,
    hasTriedAutoSync: hasTriedAutoSync.current,
    isDownloading,
    isLoaded: loadEntries ? isLoaded : staleness !== null,
    staleness,
    startSync,
  };
};
